import { useState, useEffect, useCallback } from 'react';
import emailApi from '../services/emailApi';
import logger from '../services/logger';

interface EmailSubscriber {
  id: number;
  email: string;
  subscriptionTier: string;
  subscriptionStatus: string;
  alertThreshold: number;
  categories: string[];
  digestType: string;
  language: string;
  isActive: boolean;
  isVerified: boolean;
  lastEmailSent?: string;
  emailCount: number;
  createdAt: string;
}

interface EmailTemplate {
  id: number;
  templateType: string;
  language: string;
  subject: string;
  htmlContent: string;
  textContent: string;
  availableVariables: string[];
  isActive: boolean;
  version: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

interface EmailLog {
  id: number;
  subscriberId: number;
  templateId: number;
  emailType: string;
  subject: string;
  recipientEmail: string;
  status: string;
  errorMessage?: string;
  riskScore?: number;
  articleCount?: number;
  categoriesIncluded: string[];
  sentAt?: string;
  deliveredAt?: string;
  openedAt?: string;
  clickedAt?: string;
  subscriber?: { email: string };
  template?: { templateType: string; language: string };
}

interface EmailStats {
  subscribers: {
    total: number;
    active: number;
    verified: number;
  };
  preferences: {
    alertsEnabled: number;
    digestEnabled: number;
    bothEnabled: number;
  };
  emailsSent: {
    today: number;
    thisWeek: number;
    thisMonth: number;
  };
  performance: {
    avgOpenRate: number;
  };
}

export function useEmailSubscribers(filters: any = {}) {
  const [subscribers, setSubscribers] = useState<EmailSubscriber[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      logger.debug('EMAIL_ADMIN', 'Fetching subscribers', { filters });
      const response = await emailApi.getSubscribers(filters);
      const mapped = (response.subscribers || []).map((s: any) => {
        const hasAlerts = !!s.enableAlerts;
        const hasDigest = !!s.enableDigest;
        const digestType = hasAlerts && hasDigest ? 'alerts+digest' : hasAlerts ? 'alerts' : hasDigest ? 'digest' : 'none';
        const categories: string[] = Array.isArray(s.alertCategories) && s.alertCategories.length > 0
          ? s.alertCategories
          : Array.isArray(s.digestCategories)
            ? s.digestCategories
            : [];
        return {
          id: s.id,
          email: s.email,
          subscriptionTier: s.subscriptionTier,
          subscriptionStatus: s.subscriptionStatus,
          alertThreshold: s.alertThreshold,
          categories,
          digestType,
          language: s.language,
          isActive: s.isActive,
          isVerified: s.isVerified,
          lastEmailSent: s.lastEmailSent || undefined,
          emailCount: s.emailCount || 0,
          createdAt: s.createdAt,
        } as EmailSubscriber;
      });
      setSubscribers(mapped);
      setTotal(response.total);
      logger.info('EMAIL_ADMIN', 'Subscribers fetched successfully', { count: response.total });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch subscribers';
      logger.error('EMAIL_ADMIN', 'Failed to fetch subscribers', { error: err, filters });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const deleteSubscriber = async (id: number) => {
    try {
      logger.info('EMAIL_ADMIN', 'Deleting subscriber', { subscriberId: id });
      await emailApi.deleteSubscriber(id);
      logger.info('EMAIL_ADMIN', 'Subscriber deleted successfully', { subscriberId: id });
      await fetchSubscribers();
    } catch (err) {
      logger.error('EMAIL_ADMIN', 'Failed to delete subscriber', { subscriberId: id, error: err });
      throw err;
    }
  };

  const exportSubscribers = async () => {
    try {
      logger.info('EMAIL_ADMIN', 'Exporting subscribers');
      const response = await fetch('/api/admin/email/subscribers/export', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'subscribers.csv';
      a.click();
      logger.info('EMAIL_ADMIN', 'Subscribers exported successfully');
    } catch (err) {
      logger.error('EMAIL_ADMIN', 'Failed to export subscribers', { error: err });
      throw err;
    }
  };

  return { 
    subscribers, 
    total, 
    loading, 
    error, 
    refetch: fetchSubscribers,
    deleteSubscriber,
    exportSubscribers,
  };
}

export function useEmailTemplates() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      logger.debug('EMAIL_ADMIN', 'Fetching templates');
      const response = await emailApi.getTemplates();
      setTemplates(response);
      logger.info('EMAIL_ADMIN', 'Templates fetched successfully', { count: response.length });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch templates';
      logger.error('EMAIL_ADMIN', 'Failed to fetch templates', { error: err });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const updateTemplate = async (id: number, data: Partial<EmailTemplate>) => {
    try {
      logger.info('EMAIL_ADMIN', 'Updating template', { templateId: id, changes: Object.keys(data) });
      await emailApi.updateTemplate(id, data);
      logger.info('EMAIL_ADMIN', 'Template updated successfully', { templateId: id });
      await fetchTemplates();
    } catch (err) {
      logger.error('EMAIL_ADMIN', 'Failed to update template', { templateId: id, error: err });
      throw err;
    }
  };

  const createTemplate = async (data: Partial<EmailTemplate>) => {
    try {
      logger.info('EMAIL_ADMIN', 'Creating template', { templateType: data.templateType, language: data.language });
      await emailApi.createTemplate(data);
      logger.info('EMAIL_ADMIN', 'Template created successfully', { templateType: data.templateType });
      await fetchTemplates();
    } catch (err) {
      logger.error('EMAIL_ADMIN', 'Failed to create template', { templateData: data, error: err });
      throw err;
    }
  };

  return { 
    templates, 
    loading, 
    error, 
    refetch: fetchTemplates,
    updateTemplate,
    createTemplate,
  };
}

export function useEmailLogs(filters: any = {}) {
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      logger.debug('EMAIL_ADMIN', 'Fetching email logs', { filters });
      const response = await emailApi.getLogs(filters);
      setLogs(response.logs);
      setTotal(response.total);
      logger.info('EMAIL_ADMIN', 'Email logs fetched successfully', { count: response.total });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch logs';
      logger.error('EMAIL_ADMIN', 'Failed to fetch email logs', { error: err, filters });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return { logs, total, loading, error, refetch: fetchLogs };
}

export function useEmailStats() {
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      
      try {
        logger.debug('EMAIL_ADMIN', 'Fetching email stats');
        const response = await emailApi.getStats();
        setStats(response);
        logger.info('EMAIL_ADMIN', 'Email stats fetched successfully', { 
          totalSubscribers: response.subscribers.total,
          emailsSentToday: response.emailsSent.today 
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch stats';
        logger.error('EMAIL_ADMIN', 'Failed to fetch email stats', { error: err });
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return { stats, loading, error };
}

export function useEmailQueue() {
  const [queue, setQueue] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      logger.debug('EMAIL_ADMIN', 'Fetching email queue');
      const response = await emailApi.getQueueStatus();
      setQueue(response);
      logger.info('EMAIL_ADMIN', 'Email queue fetched successfully', { queueSize: response.length });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch queue';
      logger.error('EMAIL_ADMIN', 'Failed to fetch email queue', { error: err });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const retryEmail = async (id: number) => {
    try {
      logger.info('EMAIL_ADMIN', 'Retrying email', { queueItemId: id });
      await emailApi.retryQueueItem(id);
      logger.info('EMAIL_ADMIN', 'Email retry successful', { queueItemId: id });
      await fetchQueue();
    } catch (err) {
      logger.error('EMAIL_ADMIN', 'Failed to retry email', { queueItemId: id, error: err });
      throw err;
    }
  };

  return { queue, loading, error, refetch: fetchQueue, retryEmail };
}

export async function sendTestEmail(email: string, templateType: string) {
  try {
    logger.info('EMAIL_ADMIN', 'Sending test email', { email, templateType });
    const result = await emailApi.sendTestEmail(email, templateType);
    logger.info('EMAIL_ADMIN', 'Test email sent successfully', { email, templateType });
    return result;
  } catch (err) {
    logger.error('EMAIL_ADMIN', 'Failed to send test email', { email, templateType, error: err });
    throw err;
  }
}
